import { createContext, useContext, useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import {
  loginUser as loginUserAPI,
  loginUniversityUser as loginUniversityUserAPI,
  loginCompanyUser as loginCompanyUserAPI,
  loginPlatformUser as loginPlatformUserAPI,
  getUserInfo,
  clearTokenCookies,
  getTokenFromCookie,
  isTokenExpired,
  isTokenExpiringSoon,
  getJWTData,
  refreshAccessToken,
} from "../api/services/authService";

const AuthContext = createContext();

const buildUser = (profileData, jwtData) => ({
  email: profileData.email,
  username: profileData.preferred_username,
  name:
    profileData.given_name && profileData.family_name
      ? `${profileData.given_name} ${profileData.family_name}`
      : profileData.preferred_username,
  given_name: profileData.given_name || "",
  family_name: profileData.family_name || "",
  organization_name: profileData.organization_name || "",
  organization_id: profileData.organization_id || null,
  organization_role: profileData.organization_role || "",
  roles: jwtData.realm_access?.roles || [],
  permissions: profileData.permissions || {},
  team_ids: profileData.team_ids || [],
  team_id: profileData.team_id || null,
  sub: jwtData.sub,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.sub,
        email: user.email,
        username: user.username,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Re-hydrate auth state on app load/refresh
  useEffect(() => {
    const initializeAuth = async () => {
      const accessToken = getTokenFromCookie("access_token");

      if (accessToken && !isTokenExpired(accessToken)) {
        try {
          const jwtData = getJWTData();
          const profileData = await getUserInfo();

          if (profileData && jwtData) {
            setUser(buildUser(profileData, jwtData));
          }
        } catch (error) {
          console.error("Failed to initialize auth:", error);
          clearTokenCookies();
          setUser(null);
        }
      } else {
        clearTokenCookies();
        setUser(null);
      }

      setIsInitialized(true);
    };

    initializeAuth();
  }, []);

  // Auto-refresh token when it's about to expire
  useEffect(() => {
    if (!user) return;

    const checkAndRefreshToken = async () => {
      const accessToken = getTokenFromCookie("access_token");

      if (accessToken && isTokenExpiringSoon(accessToken)) {
        try {
          console.log("Token expiring soon, refreshing...");
          await refreshAccessToken();

          const jwtData = getJWTData();
          const profileData = await getUserInfo();

          if (profileData && jwtData) {
            setUser(buildUser(profileData, jwtData));
          }
        } catch (error) {
          console.error("Failed to refresh token:", error);
          setUser(null);
          clearTokenCookies();
          window.location.href = "/login";
        }
      }
    };

    const interval = setInterval(checkAndRefreshToken, 30000);
    checkAndRefreshToken();
    return () => clearInterval(interval);
  }, [user]);

  const login = async (username, password, role = "other") => {
    setIsLoading(true);
    setError(null);

    try {
      if (role === "university_user") {
        await loginUniversityUserAPI(username, password);
      } else if (role === "company_user") {
        await loginCompanyUserAPI(username, password);
      } else if (role === "platform_admin") {
        await loginPlatformUserAPI(username, password);
      } else {
        await loginUserAPI(username, password);
      }

      const jwtData = getJWTData();
      const profileData = await getUserInfo();

      if (profileData && jwtData) {
        setUser(buildUser(profileData, jwtData));
      }

      return { ok: true };
    } catch (err) {
      const errorMessage = err.message || "Login failed. Please try again.";
      setError(errorMessage);
      return { ok: false, errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear all lab progress for this user
    if (user?.username) {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(`lab_progress_${user.username}_`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    }
    setUser(null);
    clearTokenCookies();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isLoading,
        error,
        isInitialized,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
